import { Card, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';
import type { MentionData } from '@/store/mentions/types';
import { useUsersLookup } from '@/components/access/users/hooks';
import { NameAvatar } from '@/components/common/output/name';
import { memo } from 'react';

function formatMentionDate(date: string): [string, string] {
  const d = new Date(date);

  const weekday = d.toLocaleDateString([], { weekday: 'long' });
  const month = d.toLocaleDateString([], { month: 'long' });
  const day = d.toLocaleDateString([], { day: '2-digit' });

  const time = d
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s/g, '')
    .toLowerCase()
    .replace(/^0/, '');

  return [`${weekday} ${month} ${day}`, `at ${time}`];
}

function MentionCard({
  data,
  isDismissed,
}: {
  data: MentionData;
  isDismissed?: boolean;
}) {
  const users = useUsersLookup();
  const displayName = users[data.author_id]?.data.displayName || '';
  const [first = '', last = ''] = displayName.split(' ');
  const authorName = first && last ? `${first} ${last[0]}.` : displayName;
  const commentSubject = data.fullname || '';
  const [dateLine, timeLine] = formatMentionDate(data.created_at);

  return (
    <Card shadow='none' className='cursor-pointer rounded-lg hover:bg-gray-100'>
      <CardBody>
        <div className='flex w-full justify-between'>
          <div className='flex items-center gap-4'>
            <NameAvatar value={authorName} className='self-start' />
            <div className='flex-1'>
              {/* Who was mentioned in the comment/mention document */}
              <p
                className={`text-sm font-medium ${isDismissed ? 'text-default-500 line-through' : 'text-gray-900'}`}
              >
                {/* Who mentioned the person/created this mention */}
                <span>{authorName} mentioned you in</span>
              </p>
              <div className='mt-1 flex items-center gap-2 text-xs text-[#92929D]'>
                <Icon
                  icon='mdi:message-text-outline'
                  className='text-gray-600'
                  width={16}
                />
                {/* Person the comment was written about */}
                <span>{commentSubject}</span>
              </div>
            </div>
          </div>
          <div className='flex h-full items-start text-right'>
            <span className='mt-1 text-xs text-[#92929D]'>
              <span className='whitespace-nowrap'>{dateLine}</span>
              <br />
              <span className='whitespace-nowrap'>{timeLine}</span>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default memo(MentionCard);
